/**
 * Organizations Hooks
 * React Query hooks for multi-tenant organization management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { organizationsApi } from '../lib/api';

// Query keys
export const organizationKeys = {
  all: ['organizations'],
  myOrgs: () => [...organizationKeys.all, 'my'],
  detail: (id) => [...organizationKeys.all, 'detail', id],
  members: (id) => [...organizationKeys.all, 'members', id],
  roles: () => [...organizationKeys.all, 'roles'],
};

/**
 * Fetch current user's organizations
 */
export function useMyOrganizations(options = {}) {
  return useQuery({
    queryKey: organizationKeys.myOrgs(),
    queryFn: () => organizationsApi.getMyOrganizations(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes
    ...options,
  });
}

/**
 * Fetch specific organization
 */
export function useOrganization(id, options = {}) {
  return useQuery({
    queryKey: organizationKeys.detail(id),
    queryFn: () => organizationsApi.getById(id),
    enabled: !!id,
    ...options,
  });
}

/**
 * Fetch organization members
 */
export function useOrganizationMembers(orgId, options = {}) {
  return useQuery({
    queryKey: organizationKeys.members(orgId),
    queryFn: () => organizationsApi.getMembers(orgId),
    enabled: !!orgId,
    ...options,
  });
}

/**
 * Fetch available roles
 */
export function useOrganizationRoles(options = {}) {
  return useQuery({
    queryKey: organizationKeys.roles(),
    queryFn: () => organizationsApi.getRoles(),
    staleTime: 60 * 60 * 1000, // 1 hour (roles don't change often)
    ...options,
  });
}

/**
 * Create new organization
 */
export function useCreateOrganization() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data) => organizationsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.myOrgs() });
    },
  });
}

/**
 * Update organization
 */
export function useUpdateOrganization() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }) => organizationsApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: organizationKeys.myOrgs() });
    },
  });
}

/**
 * Set default organization
 */
export function useSetDefaultOrganization() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (orgId) => organizationsApi.setDefault(orgId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.myOrgs() });
    },
  });
}

/**
 * Invite user to organization
 */
export function useInviteToOrganization() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ orgId, email, role }) => organizationsApi.invite(orgId, { email, role }),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.members(orgId) });
    },
  });
}

/**
 * Accept invitation
 */
export function useAcceptInvitation() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (token) => organizationsApi.acceptInvitation(token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.myOrgs() });
    },
  });
}

/**
 * Get invitation details
 */
export function useInvitation(token, options = {}) {
  return useQuery({
    queryKey: ['invitation', token],
    queryFn: () => organizationsApi.getInvitation(token),
    enabled: !!token,
    ...options,
  });
}

